import React, { useContext, useState } from "react";
import { useIntl } from "react-intl";
import { Box, Paper, Collapse } from "@material-ui/core";

import useStyles from "./ICIPAdminScreen.style";
import { PageTitle } from "../../components/page-title/PageTitle";
import { CustomTab, CustomTabs } from "./components/Tabs";
import {
  ICIPAdminActionTypeEnum,
  ICIPAdminContext,
  ICIPAdminDispatchContext,
} from "../../context/ICIPAdminContext";
import TabPanel from "./components/TabPanel";
import LinearLoader from "./components/Loader";
import { BusinessTab } from "./tabs/business/BusinessTab";
import { PersonalTab } from "./tabs/personal/PersonalTab";
import { Header } from "./components/Header";
import { CountriesTab } from "./tabs/countries/CountriesTab";
import { CitiesTab } from "./tabs/cities/CitiesTab";
import { RaffleDrawTab } from "./tabs/raffle-draw/RaffleDrawTab";
import { useAuthenticationContext } from "../../context/UserContext";
import { AdminRoleEnum } from "../../attributes/admin-role-attributes";
import { AdminUsersTab } from "./tabs/admin-users/AdminUsersTab";
import { IcipWithdrawTarificationsTab } from "./tabs/icip-withdraw-tarification/IcipWithdrawTarificationTab";
import { PricingTab } from "./tabs/pricing/PricingTab";
import { BaseExchangeRatesTab } from "./tabs/base-exchange-rate/BaseExchangeRatesTab";
import { WithdrawTarificationsTab } from "./tabs/withdraw-tarifications/WithdrawTarificationsTab";
import { BusinessWithdrawPercentTab } from "./tabs/business-withdraw-percents/BusinessWithdrawPercentTab";
import { FaqTab } from "./tabs/faq/FaqTab";
import { PricingGroupTab } from "./tabs/pricing-group/PricingGroupTab";
import { AccountLimitTab } from "./tabs/account-limit/AccountLimitTab";
import { BaseAccountConfTab } from "./tabs/base-account-configuration/BaseAccountConfTab";
import { WithdrawRequestsTab } from "./tabs/withdrawRequestsTab/WithdrawRequestsTab";
import {ActivitiesTab} from "./tabs/activities/ActivitiesTab";

export interface ICIPAdminScreenProps {}

export const ICIPAdminScreen = (props: ICIPAdminScreenProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [value, setValue] = useState(0);
  // Context
  const [user] = useAuthenticationContext();
  const { loading, init, error } = useContext(ICIPAdminContext);
  const dispatch = useContext(ICIPAdminDispatchContext);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const renderTabs = () => {
    if (user.hasAdminRole(AdminRoleEnum.validator)) {
      return (
        <Box pl={2} pr={2}>
          <CustomTabs
            value={value}
            onChange={handleChange}
            aria-label="Admin main tabs"
          >
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.business" })}
            />
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.personalAccount" })}
            />
          </CustomTabs>
        </Box>
      );
    }

    if (user.hasAdminRole(AdminRoleEnum.raffle_manager)) {
      return (
        <Box pl={2} pr={2}>
          <CustomTabs
            value={value}
            onChange={handleChange}
            aria-label="Admin main tabs"
          >
            <CustomTab
              label={intl.formatMessage({ id: "sharedScreen.raffleDraw" })}
            />
          </CustomTabs>
        </Box>
      );
    }

    return (
      <Box pl={2} pr={2}>
        <CustomTabs
          value={value}
          onChange={handleChange}
          aria-label="Admin main tabs"
        >
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.business" })}
          />
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.personalAccount" })}
          />
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.activities" })}
          />
          <CustomTab label={intl.formatMessage({ id: "sharedScreen.users" })} />
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.countries" })}
          />
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.cities" })}
          />
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.raffleDraw" })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.personalWithdrawTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.withdrawTarificationsMenuTitle",
            })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.businessWithdrawPercentTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.withdrawPercentsMenuTitle",
            })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.icipWithdrawTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.icipWithdrawTarification",
            })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.pricingTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.pricing",
            })}
          />

          <CustomTab
            label={intl.formatMessage({
              id: "sharedScreen.exchangeRates",
            })}
          />

          <CustomTab
            label={intl.formatMessage({
              id: "sharedScreen.faq",
            })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.accountLimitsTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.accountLimits",
            })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.baseConfigurationTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.baseConfiguration",
            })}
          />
          <CustomTab
            label={intl.formatMessage({ id: "sharedScreen.withdrawRequests" })}
          />
          <CustomTab
            title={intl.formatMessage({
              id: "sharedScreen.pricingGroupTitleOver",
            })}
            label={intl.formatMessage({
              id: "sharedScreen.pricingGroupTitle",
            })}
          />
        </CustomTabs>
      </Box>
    );
  };

  const renderTabsPanel = () => {
    if (user.hasAdminRole(AdminRoleEnum.validator)) {
      return (
        <Box component="div" className="tabPanel">
          <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={0}>
            <BusinessTab />
          </TabPanel>
          <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={1}>
            <PersonalTab />
          </TabPanel>
        </Box>
      );
    }

    if (user.hasAdminRole(AdminRoleEnum.raffle_manager)) {
      return (
        <Box component="div" className="tabPanel">
          <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={0}>
            <RaffleDrawTab />
          </TabPanel>
        </Box>
      );
    }

    return (
      <Box component="div" className="tabPanel">
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={0}>
          <BusinessTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={1}>
          <PersonalTab />
        </TabPanel>
          <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={2} >
              <ActivitiesTab />
          </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={3}>
          <AdminUsersTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={4}>
          <CountriesTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={5}>
          <CitiesTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={6}>
          <RaffleDrawTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={7}>
          <WithdrawTarificationsTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={8}>
          <BusinessWithdrawPercentTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={9}>
          <IcipWithdrawTarificationsTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={10}>
          <PricingTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={11}>
          <BaseExchangeRatesTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={12}>
          <FaqTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={13}>
          <AccountLimitTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={14}>
          <BaseAccountConfTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={15}>
          <WithdrawRequestsTab />
        </TabPanel>
        <TabPanel id="icip-admin-main-tabs-tabpanel" value={value} index={16}>
          <PricingGroupTab />
        </TabPanel>
      </Box>
    );
  };

  const closeErrorWrapper = () => {
    dispatch({
      type: ICIPAdminActionTypeEnum.error,
      data: { error: undefined },
    });
  };

  return (
    <>
      <PageTitle title={intl.formatMessage({ id: "sharedScreen.admin" })} />
      <Box className={classes.root}>
        <Header />
        {!init ? (
          <Paper square elevation={0} className={classes.paperTobTabs}>
            {renderTabs()}
          </Paper>
        ) : (
          <></>
        )}

        {renderTabsPanel()}

        {loading || init ? (
          <Box component="div" className={classes.loaderWrapper}>
            <LinearLoader />
            <Box component="div" className={classes.loaderOverlay} />
          </Box>
        ) : (
          <></>
        )}

        <Box component="div" className={classes.errorWrapper}>
          <Collapse
            in={error !== undefined && error !== null}
            timeout="auto"
            unmountOnExit
          >
            <Box
              component="div"
              className={classes.errorWrapperContent}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box component="div" className="content">
                {error}
              </Box>
              <Box
                component="div"
                className="action"
                onClick={(_) => closeErrorWrapper()}
              >
                {intl.formatMessage({ id: "sharedScreen.close" })}
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </>
  );
};

ICIPAdminScreen.routeName = "/admin";
